// src/utils/compressImage.ts
// Compresses and resizes images before upload to reduce file size and improve load times.

const MAX_WIDTH = 1600;
const MAX_HEIGHT = 1600;
const JPEG_QUALITY = 0.8;

/**
 * Compresses an image file by resizing and converting to JPEG.
 * - Resizes to fit within MAX_WIDTH x MAX_HEIGHT while preserving aspect ratio
 * - Converts to JPEG at 80% quality
 * - Skips non-image files (returns original)
 * - Skips GIFs to preserve animation
 */
export async function compressImage(file: File): Promise<File> {
  // Only compress image types (skip GIF to preserve animation)
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  // Skip if already small (under 200KB)
  if (file.size < 200 * 1024) {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Skip if already within bounds and file is reasonable size (under 500KB)
      if (width <= MAX_WIDTH && height <= MAX_HEIGHT && file.size < 500 * 1024) {
        resolve(file);
        return;
      }

      // Calculate new dimensions preserving aspect ratio
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            // If compression made it bigger, use original
            resolve(file);
            return;
          }

          // Keep original extension for PNGs with transparency, otherwise use JPEG
          const outputName = file.name.replace(/\.[^.]+$/, '.jpg');
          const compressed = new File([blob], outputName, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          resolve(compressed);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // On error, return original
    };

    img.src = url;
  });
}

/**
 * Compresses a base64 data URL image (used for PDF page images).
 * Returns a compressed Blob.
 */
export async function compressDataUrl(dataUrl: string): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      // Resize if needed
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        fetch(dataUrl).then(r => r.blob()).then(resolve);
        return;
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            fetch(dataUrl).then(r => r.blob()).then(resolve);
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };

    img.onerror = () => {
      fetch(dataUrl).then(r => r.blob()).then(resolve);
    };

    img.src = dataUrl;
  });
}
